import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEO from '../components/SEO';
import BlogSidebar from '../components/BlogSidebar';

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  category: string;
}

const blogPosts: BlogPost[] = [
  {
    slug: 'supaserver-vs-firebase',
    title: 'Supaserver vs Firebase: Which Backend Should You Choose?',
    description: 'A detailed comparison of Supaserver and Firebase. Learn about their approaches to data storage, authentication, developer experience, and when to choose each platform.',
    date: 'January 2026',
    readTime: '12 min read',
    category: 'Comparison',
  },
  {
    slug: 'supaserver-vs-supabase',
    title: 'Supaserver vs Supabase: Simplicity vs Power',
    description: 'Compare Supaserver and Supabase to understand their different approaches to backend development. From schema-less JSON to PostgreSQL, find the right fit for your project.',
    date: 'January 2026',
    readTime: '11 min read',
    category: 'Comparison',
  },
  {
    slug: 'supaserver-vs-xano',
    title: 'Supaserver vs Xano: Two Approaches to No-Code Backend',
    description: 'Explore the differences between Supaserver and Xano. Compare their visual builders, API generation, pricing models, and developer workflows.',
    date: 'January 2026',
    readTime: '10 min read',
    category: 'Comparison',
  },
];

export default function BlogPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title="Blog — Supaserver"
        description="Learn about backend development, API design, and how Supaserver compares to other platforms. Tutorials, comparisons, and best practices."
        canonical="https://supaserver.app/blog"
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Blog
            </h1>
            <p className="text-xl text-gray-300">
              Comparisons, tutorials, and insights about backend development
            </p>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto flex gap-12">
            <BlogSidebar />
            <div className="flex-1 min-w-0 grid gap-8">
              {blogPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group block bg-white rounded-2xl border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                      {post.title}
                    </h2>
                    
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">
                      {post.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{post.date}</span>
                        <span>•</span>
                        <span>{post.readTime}</span>
                      </div>
                      <div className="flex items-center text-orange-600 text-sm font-medium">
                        <span>Read article</span>
                        <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to try Supaserver?
            </h2>
            <p className="text-gray-600 mb-6">
              Create your first API endpoint in seconds. No signup required.
            </p>
            <Link
              to="/api-request?demo=true"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-full font-medium hover:bg-orange-600 transition-colors"
            >
              <span>Try the Demo</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
